import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { Scale, Shield, Cookie, FileText, Mail, ExternalLink } from 'lucide-react';

const SECTIONS = [
  { id: 'mentions', label: 'Mentions légales', icon: FileText },
  { id: 'rgpd', label: 'Confidentialité (RGPD)', icon: Shield },
  { id: 'cookies', label: 'Cookies', icon: Cookie },
  { id: 'cgu', label: 'CGU', icon: Scale },
];

function Mentions() {
  return (
    <div className="prose-cv">
      <h2>Mentions légales &amp; positionnement</h2>
      <p className="text-white/55 text-[12.5px] uppercase tracking-wider font-mono-spec">
        Conformément à l'article 6 de la LCEN (n°2004-575) et au RGPD · Dernière mise à jour : 01 juillet 2026
      </p>

      <h3>1. Mission &amp; positionnement</h3>
      <p>
        <strong>Code Voiture est une plateforme éditoriale et pédagogique indépendante</strong> dédiée à la sécurité routière et à la connaissance du Code de la route (permis voiture). Elle a été conçue dans une démarche de <strong>transfert de savoir à but non lucratif</strong> par <strong>Bruno LLORENS</strong>, enseignant de la conduite et de la sécurité routière (ECSR) diplômé d'État, exerçant à Bourgoin-Jallieu (Nord Isère).
      </p>
      <p>
        <strong>Code Voiture N'EST PAS une auto-école</strong> : aucune activité commerciale d'enseignement de la conduite n'y est exercée, aucun agrément préfectoral d'enseignement n'y est attaché. La plateforme ne forme pas d'élèves, ne délivre pas de livret d'apprentissage officiel et ne présente aucun candidat à l'examen.
      </p>
      <p>
        <strong>Code Voiture n'organise pas, ne vend pas et ne réserve aucun passage de l'examen officiel.</strong> L'épreuve théorique générale (ETG) s'effectue exclusivement auprès des opérateurs privés agréés par l'État :
      </p>
      <ul>
        <li><a href="https://www.lecode.laposte.fr/" target="_blank" rel="noopener noreferrer">La Poste</a></li>
        <li><a href="https://www.objectifcode.sgs.com/" target="_blank" rel="noopener noreferrer">SGS</a></li>
        <li><a href="https://www.dekra-exacode.fr/" target="_blank" rel="noopener noreferrer">Dekra (ExaCode)</a></li>
        <li><a href="https://www.codengo.fr/" target="_blank" rel="noopener noreferrer">Pearson VUE (Code'nGo!)</a></li>
        <li><a href="https://www.bureauveritas-codedelaroute.fr/" target="_blank" rel="noopener noreferrer">Bureau Veritas</a></li>
      </ul>
      <p>
        <strong>Aucune affiliation, partenariat ni accord</strong> avec les éditeurs commerciaux du Code de la route (Codes Rousseau®, ENPC®, Ediser®, Prevent®, Easy Systems®, et autres). Les marques citées restent la propriété de leurs détenteurs respectifs.
      </p>
      <p>
        L'utilisation de Code Voiture est <strong>entièrement gratuite</strong> pour les candidats au permis voiture (B, AAC, CS, BEA, B96, B+E). Aucun frais caché.
      </p>
      <p>
        <strong>Note :</strong> Bruno LLORENS exerce parallèlement, à titre individuel, une activité de <strong>moniteur indépendant en micro-entreprise</strong> à destination des auto-écoles partenaires. Cette activité commerciale est <strong>distincte</strong> de l'édition de la plateforme codevoiture.fr (laquelle demeure gratuite et éditoriale) et n'est accessible qu'aux établissements professionnels via contact direct.
      </p>

      <h3>2. Éditeur du site</h3>
      <table className="w-full text-[13.5px] my-5 border-collapse">
        <tbody className="text-white/75">
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top w-[180px]">Éditeur</td>
            <td className="py-2.5">Bruno LLORENS, Enseignant de la Conduite et de la Sécurité Routière (ECSR) diplômé d'État</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Statut juridique</td>
            <td className="py-2.5"><strong>Moniteur indépendant en micro-entreprise</strong> — TVA non applicable (art. 293 B du CGI). Pas d'auto-école, pas d'agrément préfectoral d'enseignement de la conduite. Activité de prestation pour établissements agréés uniquement.</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Site web</td>
            <td className="py-2.5">Plateforme <strong>éditoriale et pédagogique non lucrative</strong> (LCEN art. 6, III, 2°) — gratuite, sans publicité, sans revente de données.</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Marque éditoriale</td>
            <td className="py-2.5">« LLB ECSR » — pour <strong>L</strong>LORENS <strong>B</strong>runo, <strong>E</strong>nseignant de la <strong>C</strong>onduite et de la <strong>S</strong>écurité <strong>R</strong>outière</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Qualifications</td>
            <td className="py-2.5">TP ECSR · CCS 2 Roues · Autorisation d'enseigner en cours de validité</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Adresse pro</td>
            <td className="py-2.5">Bourgoin-Jallieu, Nord Isère (38), France</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Responsable publication</td>
            <td className="py-2.5">Bruno LLORENS</td>
          </tr>
          <tr>
            <td className="py-2.5 pr-4 text-white/55 font-mono-spec text-[12px] uppercase tracking-wider align-top">Contact</td>
            <td className="py-2.5"><a href="mailto:contact@codevoiture.fr">contact@codevoiture.fr</a></td>
          </tr>
        </tbody>
      </table>
      <p className="text-white/60 italic">
        <strong>Code Voiture n'est pas une auto-école.</strong> Il s'agit d'une plateforme éditoriale indépendante, sans agrément préfectoral d'enseignement de la conduite. Aucune affiliation avec les éditeurs Codes Rousseau®, ENPC®, Ediser® ou autres marques d'édition du code de la route.
      </p>

      <h3>3. Hébergement</h3>
      <p>
        Le site Code Voiture est hébergé sur l'infrastructure cloud opérée par <strong>Emergent / Kubernetes</strong>. Le nom de domaine <strong>codevoiture.fr</strong> est enregistré auprès de <strong>OVH SAS</strong> (2 rue Kellermann, 59100 Roubaix — France · <a href="https://www.ovhcloud.com/fr/" target="_blank" rel="noopener noreferrer">ovhcloud.com</a>).
      </p>

      <h3>4. Contenu pédagogique &amp; sources</h3>
      <p>
        L'ensemble des questions, fiches, enquêtes et schémas pédagogiques publiés sur Code Voiture est <strong>rédigé de manière originale</strong> par Bruno LLORENS (ECSR), à partir <strong>exclusivement de sources officielles publiquement accessibles</strong> :
      </p>
      <ul>
        <li>Le <strong>Code de la route</strong> en vigueur (Légifrance).</li>
        <li>Le programme officiel public de l'<strong>Épreuve Théorique Générale (ETG)</strong> — arrêté du 20 avril 2012 modifié.</li>
        <li>Le <strong>REMC</strong> (Référentiel pour l'Éducation à une Mobilité Citoyenne) — arrêté du 13 mai 2013 modifié.</li>
        <li>Les <strong>arrêtés ministériels</strong> régissant les formations complémentaires (B96 du 08/01/2013, B+E, BEA, Conduite Accompagnée, Conduite Supervisée).</li>
        <li>Les <strong>données ONISR</strong> (Observatoire National Interministériel de la Sécurité Routière), <strong>ANTAI</strong>, <strong>Service-Public.fr</strong> et les études IFSTTAR / Cerema publiquement consultables.</li>
      </ul>
      <p>
        <strong>Aucun contenu n'est repris, copié ou adapté</strong> des banques de questions ou manuels édités par les acteurs commerciaux du Code de la route (Codes Rousseau®, ENPC®, Ediser®, Prevent®, Easy Systems®, etc.). Code Voiture n'a aucun accord, licence ou affiliation avec ces éditeurs.
      </p>

      <h3>5. Propriété intellectuelle</h3>
      <p>
        L'ensemble des contenus présents sur Code Voiture (textes, questions, illustrations, schémas, code source, charte graphique) est protégé par les dispositions du Code de la propriété intellectuelle et reste la propriété exclusive de Bruno LLORENS (LLB ECSR).
      </p>
      <p>
        Toute reproduction, représentation, modification, publication ou adaptation, totale ou partielle, est strictement interdite sans autorisation écrite préalable, à l'exception des usages strictement personnels des candidats utilisant la plateforme à des fins de révision. L'utilisation pédagogique en classe (mode présentation, projection) par un enseignant ECSR est <strong>libre et gratuite</strong>.
      </p>

      <h3>6. Limitation de responsabilité</h3>
      <p>
        Code Voiture est un <strong>outil pédagogique d'entraînement et de préparation</strong>. La réussite à l'examen officiel de l'ETG et l'obtention du permis dépendent du candidat, de sa formation pratique en auto-école agréée, et des conditions du jour de l'examen passé auprès d'un opérateur agréé par l'État.
      </p>
      <p>
        Bruno LLORENS (LLB ECSR) ne saurait être tenu responsable d'un échec à l'examen officiel, ni d'une utilisation non conforme de la plateforme.
      </p>

      <h3>6bis. Fiabilité du contenu, IA &amp; évolution de la législation</h3>
      <p className="font-semibold text-white/90">Avertissement essentiel</p>
      <p>
        Les questions, réponses, explications et contenus pédagogiques diffusés sur Code Voiture sont rédigés à des <strong>fins de préparation à l'examen uniquement</strong>. Ils ne constituent <strong>ni un avis juridique, ni une interprétation officielle</strong> des textes de loi. En cas de doute, de question précise ou de litige, seul fait foi le <strong>Code de la route publié sur Légifrance</strong> et les éventuelles décisions de justice qui s'y rattachent.
      </p>
      <p className="font-semibold text-white/90">🛡️ Méthode d'élaboration des contenus</p>
      <p>
        Les contenus sont <strong>élaborés, relus et validés par des Enseignants de la Conduite et de la Sécurité Routière (ECSR)</strong> diplômés d'État. Pour accélérer l'enrichissement pédagogique (rédaction d'explications, pièges, moyens mnémotechniques, fiches thématiques), Code Voiture utilise également <strong>des outils d'intelligence artificielle</strong> (modèles de langage), dont les productions font systématiquement l'objet d'un <strong>contrôle humain par un ECSR</strong> avant publication. Cette démarche est explicitée ici par souci de transparence.
      </p>
      <p className="font-semibold text-white/90">⚖️ Évolution permanente du Code de la route</p>
      <p>
        Le Code de la route, les arrêtés, décrets et normes européennes <strong>évoluent régulièrement</strong> (ex. permis dès 17 ans depuis le 01/01/2024, dématérialisation partielle du permis en 2025, révision des ZFE, examen ETG rénové, etc.). Malgré les efforts quotidiens de mise à jour de la plateforme, <strong>un décalage temporaire peut exister</strong> entre le contenu publié et la législation en vigueur le jour de votre consultation ou de votre examen.
      </p>
      <p className="font-semibold text-white/90">❌ Exclusion de responsabilité pour erreurs ou désuétudes</p>
      <p>
        Bruno LLORENS (LLB ECSR) et ses collaborateurs s'efforcent d'assurer <strong>l'exactitude, la complétude et l'actualité</strong> des informations publiées. Toutefois, malgré ces efforts, <strong>aucune garantie</strong> n'est donnée à ce titre. La responsabilité de l'éditeur <strong>ne saurait être engagée</strong> au titre :
      </p>
      <ul>
        <li>d'une <strong>erreur factuelle, typographique ou d'interprétation</strong> présente dans une question, une réponse ou une explication ;</li>
        <li>d'un <strong>contenu devenu obsolète</strong> suite à une évolution législative, réglementaire ou normative postérieure à sa publication ;</li>
        <li>d'une <strong>divergence entre nos contenus et le sujet officiel</strong> présenté le jour de l'Épreuve Théorique Générale (ETG) par l'un des opérateurs agréés par l'État ;</li>
        <li>d'une <strong>décision personnelle</strong> (administrative, juridique, comportement routier) que l'utilisateur prendrait sur la seule base des contenus de Code Voiture <strong>sans vérification</strong> sur les sources officielles.</li>
      </ul>
      <p className="font-semibold text-white/90">✅ Signalement d'une erreur ou d'une évolution</p>
      <p>
        Toute erreur, imprécision ou évolution législative peut être <strong>signalée à l'éditeur</strong> à l'adresse <a href="mailto:contact@codevoiture.fr?subject=Signalement%20de%20contenu">contact@codevoiture.fr</a>. Les signalements sont examinés sous 72 heures ouvrées. L'éditeur se réserve le droit de <strong>corriger, retirer ou mettre à jour</strong> un contenu à tout moment, sans préavis ni indemnité.
      </p>
      <p className="font-semibold text-white/90">📖 Sources officielles à consulter</p>
      <ul>
        <li><a href="https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/" target="_blank" rel="noopener noreferrer">Code de la route sur Légifrance</a> — source de référence pour les textes en vigueur</li>
        <li><a href="https://www.securite-routiere.gouv.fr/" target="_blank" rel="noopener noreferrer">Sécurité routière (gouv.fr)</a> — campagnes et recommandations officielles</li>
        <li><strong>Votre auto-école agréée</strong> — interlocuteur pédagogique de référence pour votre formation</li>
      </ul>
      <p>
        En utilisant Code Voiture, vous reconnaissez avoir pris connaissance de ces limitations et acceptez de vous référer aux sources officielles en cas de doute ou d'évolution législative.
      </p>

      <h3>7. Données personnelles</h3>
      <p>
        Le traitement des données personnelles fait l'objet d'une <strong>politique de confidentialité dédiée</strong> (onglet « Confidentialité (RGPD) » ci-contre), conforme au RGPD (règlement UE 2016/679) et à la loi Informatique et Libertés.
      </p>

      <h3>8. Droit applicable</h3>
      <p>
        Le site et son utilisation sont régis par le <strong>droit français</strong>. Tout litige relatif à son utilisation relève de la compétence exclusive des tribunaux français.
      </p>

      <p className="text-white/45 text-[12px] mt-8 pt-6 border-t border-white/10">
        © 2026 Bruno LLORENS (LLB ECSR) — Code Voiture. Tous droits réservés.
      </p>
    </div>
  );
}

function RGPD() {
  return (
    <div className="prose-cv">
      <h2>Politique de confidentialité</h2>
      <p className="text-white/55 text-[12.5px] uppercase tracking-wider font-mono-spec">
        Conformément au RGPD (UE 2016/679) et à la loi Informatique et Libertés du 6 janvier 1978 modifiée · Dernière mise à jour : 01 juillet 2026
      </p>

      <h3>1. Responsable de traitement</h3>
      <p>
        Le responsable du traitement des données personnelles collectées sur Code Voiture est <strong>Bruno LLORENS</strong>, Enseignant de la Conduite et de la Sécurité Routière (ECSR) — exploitant la plateforme sous l'identité éditoriale <strong>« LLB ECSR »</strong> — exerçant à Bourgoin-Jallieu, Nord Isère (38). Contact : <a href="mailto:contact@codevoiture.fr">contact@codevoiture.fr</a>.
      </p>

      <h3>2. Données collectées &amp; finalités</h3>

      <p className="font-semibold text-white/90">a) Données de compte (candidat ou enseignant)</p>
      <ul>
        <li>Email, mot de passe (haché bcrypt), prénom, nom, rôle (élève / enseignant / admin)</li>
        <li><strong>Finalité :</strong> création et gestion du compte, authentification</li>
        <li><strong>Base légale :</strong> exécution du contrat (CGU)</li>
      </ul>

      <p className="font-semibold text-white/90">b) Données de progression pédagogique</p>
      <ul>
        <li>Réponses aux questions, scores aux examens blancs, modules complétés, sessions de révision, livret d'apprentissage numérique</li>
        <li><strong>Finalité :</strong> personnalisation de l'apprentissage, suivi de la progression, validation par l'enseignant ECSR lié</li>
        <li><strong>Base légale :</strong> intérêt légitime du candidat</li>
      </ul>

      <p className="font-semibold text-white/90">c) Mesure d'audience anonymisée (PostHog)</p>
      <ul>
        <li>Pages visitées, parcours utilisateur, durée de session, type d'appareil</li>
        <li><strong>Finalité :</strong> amélioration de l'ergonomie et détection des bugs</li>
        <li><strong>Base légale :</strong> intérêt légitime, données pseudonymisées</li>
      </ul>

      <h3>3. Sous-traitants &amp; transferts</h3>
      <table className="w-full text-[13.5px] my-5 border-collapse">
        <thead>
          <tr className="border-b border-white/15">
            <th className="text-left py-3 pr-4 text-white font-semibold">Sous-traitant</th>
            <th className="text-left py-3 pr-4 text-white font-semibold">Finalité</th>
            <th className="text-left py-3 text-white font-semibold">Localisation</th>
          </tr>
        </thead>
        <tbody className="text-white/70">
          <tr className="border-b border-white/5">
            <td className="py-3 pr-4">Resend</td>
            <td className="py-3 pr-4">Envoi des emails transactionnels</td>
            <td className="py-3 font-mono-spec">USA (CCT)</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-3 pr-4">OpenAI (via Emergent)</td>
            <td className="py-3 pr-4">Coach IA pédagogique (GPT-4o-mini)</td>
            <td className="py-3 font-mono-spec">USA (CCT)</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-3 pr-4">PostHog</td>
            <td className="py-3 pr-4">Mesure d'audience anonymisée</td>
            <td className="py-3 font-mono-spec">USA (CCT)</td>
          </tr>
          <tr>
            <td className="py-3 pr-4">OVH SAS</td>
            <td className="py-3 pr-4">Enregistrement du nom de domaine</td>
            <td className="py-3 font-mono-spec">France (UE)</td>
          </tr>
        </tbody>
      </table>
      <p><strong>Aucune donnée n'est revendue à des tiers. Aucun profilage publicitaire.</strong></p>

      <h3>4. Durées de conservation</h3>
      <ul>
        <li><strong>Compte actif :</strong> tant qu'utilisé. Suppression auto après 24 mois d'inactivité.</li>
        <li><strong>Données de progression :</strong> liées à la durée de vie du compte.</li>
        <li><strong>Logs techniques :</strong> 12 mois maximum.</li>
        <li><strong>Mesure PostHog :</strong> 13 mois maximum (recommandation CNIL).</li>
      </ul>

      <h3>5. Vos droits RGPD</h3>
      <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
      <ul>
        <li>Droit d'accès, de rectification et d'effacement (« droit à l'oubli »)</li>
        <li>Droit à la limitation et à la portabilité du traitement</li>
        <li>Droit d'opposition au traitement, notamment la mesure d'audience</li>
        <li>Droit d'introduire une réclamation auprès de la <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer">CNIL</a></li>
      </ul>
      <p>
        Pour exercer vos droits : <a href="mailto:contact@codevoiture.fr?subject=RGPD%20-%20Demande%20d%27exercice%20de%20droit">contact@codevoiture.fr</a> — réponse sous 30 jours maximum.
      </p>

      <h3>6. Cookies</h3>
      <p>
        Code Voiture utilise uniquement des cookies <strong>strictement nécessaires</strong> au fonctionnement (jeton de session JWT, préférence de thème clair/sombre) et un outil de mesure d'audience pseudonymisée (PostHog), exempté de consentement préalable selon les recommandations de la CNIL.
      </p>
      <p>Aucun cookie publicitaire ni de profilage tiers n'est déposé.</p>

      <p className="text-white/45 text-[12px] mt-8 pt-6 border-t border-white/10">
        © 2026 Bruno LLORENS (LLB ECSR) — Code Voiture. Tous droits réservés.
      </p>
    </div>
  );
}

function Cookies() {
  return (
    <div className="prose-cv">
      <h2>Politique des cookies</h2>
      <p className="text-white/55 text-[12.5px] uppercase tracking-wider font-mono-spec">Dernière mise à jour : juin 2026</p>

      <h3>Notre engagement : zéro cookie commercial</h3>
      <p>
        Code Voiture <strong>n'utilise aucun cookie publicitaire, analytique commercial, ou de tracking comportemental</strong>. Nous n'avons donc pas besoin de bandeau de consentement intrusif.
      </p>

      <h3>Les seuls cookies utilisés</h3>
      <table className="w-full text-[13.5px] my-5 border-collapse">
        <thead>
          <tr className="border-b border-white/15">
            <th className="text-left py-3 pr-4 text-white font-semibold">Nom</th>
            <th className="text-left py-3 pr-4 text-white font-semibold">Finalité</th>
            <th className="text-left py-3 text-white font-semibold">Durée</th>
          </tr>
        </thead>
        <tbody className="text-white/70">
          <tr className="border-b border-white/5">
            <td className="py-3 pr-4 font-mono-spec text-white/90">cv_token</td>
            <td className="py-3 pr-4">Garde l'utilisateur connecté à son compte (JWT en localStorage)</td>
            <td className="py-3 font-mono-spec">Session navigateur</td>
          </tr>
          <tr className="border-b border-white/5">
            <td className="py-3 pr-4 font-mono-spec text-white/90">cv_pref_*</td>
            <td className="py-3 pr-4">Préférences d'affichage (mode présentation, masquage bannières)</td>
            <td className="py-3 font-mono-spec">12 mois</td>
          </tr>
        </tbody>
      </table>

      <p>Ces deux stockages sont <strong>strictement nécessaires</strong> au fonctionnement du service (article 82 de la loi Informatique et Libertés modifiée) — ils ne nécessitent donc pas de consentement préalable.</p>

      <h3>Refuser les cookies</h3>
      <p>
        Vous pouvez à tout moment supprimer ces stockages via les paramètres de confidentialité de votre navigateur. Conséquence : vous serez déconnecté(e) et vos préférences d'affichage seront réinitialisées. Les contenus du site restent <strong>intégralement accessibles</strong> sans aucun cookie.
      </p>

      <h3>Outils externes embarqués</h3>
      <p>
        Certaines pages embarquent des vidéos YouTube (chaîne officielle Sécurité Routière). YouTube peut déposer ses propres cookies dès qu'une vidéo est lue. Nous utilisons systématiquement le domaine <strong>youtube-nocookie.com</strong> lorsque disponible. Vous pouvez consulter la politique YouTube : <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.
      </p>
    </div>
  );
}

function CGU() {
  return (
    <div className="prose-cv">
      <h2>Conditions Générales d'Utilisation</h2>
      <p className="text-white/55 text-[12.5px] uppercase tracking-wider font-mono-spec">Dernière mise à jour : juin 2026</p>

      <h3>1. Objet</h3>
      <p>
        Les présentes CGU régissent l'accès et l'utilisation du site <strong>codevoiture.fr</strong>, plateforme pédagogique gratuite à destination des candidats au permis voiture (B, AAC, CS, BEA, B96, B+E) et des enseignants ECSR.
      </p>

      <h3>2. Accès au service</h3>
      <p>L'accès au site est <strong>gratuit, sans inscription obligatoire pour les contenus publics</strong>. La création d'un compte permet d'accéder au livret d'apprentissage et de lier un enseignant.</p>

      <h3>3. Engagements de l'utilisateur</h3>
      <ul>
        <li>Ne pas utiliser le site à des fins commerciales sans autorisation préalable.</li>
        <li>Ne pas tenter d'accéder aux comptes d'autres utilisateurs.</li>
        <li>Ne pas saturer le service (scraping massif, attaques par déni de service).</li>
        <li>Fournir des informations exactes lors de la création de compte.</li>
      </ul>

      <h3>4. Compte enseignant</h3>
      <p>
        L'inscription en tant qu'enseignant ECSR fait l'objet d'une <strong>vérification manuelle</strong> par l'éditeur (validation sous 48 h ouvrées). Seuls les titulaires effectifs du titre ECSR ou autorisations d'enseigner sont éligibles.
      </p>

      <h3>5. Modération</h3>
      <p>L'éditeur se réserve le droit de suspendre tout compte ne respectant pas les présentes CGU, après notification par email lorsque cela est possible.</p>

      <h3>6. Modification des CGU</h3>
      <p>Les présentes CGU peuvent être modifiées à tout moment. Les utilisateurs connectés en seront notifiés par email. La poursuite de l'utilisation après modification vaut acceptation.</p>

      <h3>7. Contact</h3>
      <p>Pour toute question : <a href="mailto:contact@codevoiture.fr">contact@codevoiture.fr</a></p>
    </div>
  );
}

export default function MentionsLegalesPage() {
  const location = useLocation();
  const [section, setSection] = useState('mentions');

  // Sync tab with URL: /rgpd or /confidentialite → RGPD, /cookies → cookies, /cgu → cgu, else mentions
  useEffect(() => {
    const p = location.pathname;
    if (p === '/rgpd' || p === '/confidentialite') setSection('rgpd');
    else if (p === '/cookies') setSection('cookies');
    else if (p === '/cgu') setSection('cgu');
    else setSection('mentions');
  }, [location.pathname]);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle
          kicker="Légal · Code Voiture"
          title="Mentions légales, RGPD, cookies & CGU"
          sub="Transparence totale sur l'éditeur, le traitement des données et les conditions d'utilisation de la plateforme."
        />

        <div className="grid lg:grid-cols-[260px_1fr] gap-5 sm:gap-8">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 hide-scroll">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    data-testid={`legal-tab-${s.id}`}
                    onClick={() => setSection(s.id)}
                    className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 h-10 sm:h-12 rounded-xl text-[12.5px] sm:text-[13.5px] font-semibold border transition shrink-0 lg:w-full text-left whitespace-nowrap ${section === s.id ? 'border-red-500 bg-red-950/30 text-white' : 'border-white/10 bg-white/[0.03] text-white/65 hover:border-white/20'}`}
                  >
                    <Icon size={15} className="shrink-0" /> {s.label}
                  </button>
                );
              })}
            </div>
            <div className="hidden lg:block mt-6 p-4 rounded-xl border border-white/10 bg-[#0d0d0e] text-[12.5px]">
              <div className="flex items-center gap-2 text-red-400 font-bold uppercase tracking-wider text-[10.5px] mb-2"><Mail size={12} /> Contact RGPD</div>
              <a href="mailto:contact@codevoiture.fr?subject=RGPD" className="text-white/80 hover:text-white break-all">contact@codevoiture.fr</a>
            </div>
          </aside>

          {/* Content */}
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7 lg:p-10" data-testid={`legal-content-${section}`}>
            {section === 'mentions' && <Mentions />}
            {section === 'rgpd' && <RGPD />}
            {section === 'cookies' && <Cookies />}
            {section === 'cgu' && <CGU />}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
